import type { ReactNode } from 'react';

/**
 * O «Trecho» do card 1click (#5279, Figma final 17/10): a gramática comum ao
 * aéreo («Trecho do voo») e ao ônibus («Trecho do ônibus»). Cabeçalho com o
 * glifo do sentido, a data e o sentido; as pontas com a hora e, quando há, a
 * sigla e o lugar; o trilho com o glifo do modal (ou a pill das paradas) e a
 * duração embaixo; a régua com a tarifa/classe e os links do trecho.
 */
interface TripLegPoint {
  readonly time: string;
  /** Sigla (IATA no voo); o ônibus não tem. */
  readonly code?: string;
  /** Cidade (voo) ou rodoviária (ônibus); some quando não se sabe. */
  readonly place?: string;
}

export interface CheckoutTripLegProps {
  readonly icon: ReactNode;
  readonly date: string;
  /** «Ida», «Volta», «Trecho 2». */
  readonly direction: string;
  /** A identidade do operador à direita: logo + nº do voo, viação · classe. */
  readonly operator?: ReactNode;
  readonly departure: TripLegPoint;
  readonly arrival: TripLegPoint;
  /** «+1» quando a chegada cai noutro dia, com o nome acessível. */
  readonly arrivalDayOffset?: { readonly days: number; readonly label: string };
  /** O meio do trilho: o glifo do modal, ou a pill das paradas. */
  readonly rail: ReactNode;
  readonly duration?: string;
  /** A régua: tarifa/classe à esquerda. */
  readonly rule?: string;
  /** Os links da régua, à direita. */
  readonly links?: ReactNode;
}

const META = 'text-xs leading-4';
const TIME = 'text-lg leading-7 font-medium text-foreground tabular-nums';
const CODE = 'text-sm leading-5 font-medium text-foreground';

export function CheckoutTripLeg({
  icon,
  date,
  direction,
  operator,
  departure,
  arrival,
  arrivalDayOffset,
  rail,
  duration,
  rule,
  links,
}: CheckoutTripLegProps) {
  const withCodes = Boolean(departure.code ?? arrival.code);
  return (
    <div className="flex min-w-0 flex-col">
      <div className={`flex h-5 min-w-0 items-center gap-1 ${META}`}>
        <span className="flex shrink-0 text-foreground" aria-hidden>
          {icon}
        </span>
        <span className="shrink-0 font-semibold text-foreground">{date}</span>
        <span className="shrink-0 font-medium text-muted-foreground">· {direction}</span>
        {operator && (
          <span className="ml-auto flex min-w-0 items-center gap-1.5 text-muted-foreground">
            {operator}
          </span>
        )}
      </div>
      <div className="mt-1 grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-2">
        <span className={TIME}>{departure.time}</span>
        <span className="relative flex h-6 items-center justify-center">
          <span aria-hidden className="absolute inset-x-0 top-1/2 border-t border-foreground/10" />
          <span className="relative flex items-center bg-card px-1.5 text-foreground">{rail}</span>
        </span>
        <span className={`${TIME} flex items-baseline justify-end gap-0.5`}>
          {arrival.time}
          {arrivalDayOffset && arrivalDayOffset.days > 0 && (
            <span
              className="text-xs leading-4 font-medium text-muted-foreground"
              role="img"
              title={arrivalDayOffset.label}
              aria-label={arrivalDayOffset.label}
            >
              +{arrivalDayOffset.days}
            </span>
          )}
        </span>
        {(withCodes || duration) && (
          <>
            <span className={CODE}>{departure.code}</span>
            <span className={`${META} text-center text-foreground tabular-nums`}>{duration}</span>
            <span className={`${CODE} text-right`}>{arrival.code}</span>
          </>
        )}
      </div>
      {(departure.place ?? arrival.place) && (
        <div className={`flex gap-2 ${META} text-foreground`}>
          <span className="min-w-0 flex-1 truncate" title={departure.place}>
            {departure.place}
          </span>
          <span className="min-w-0 flex-1 truncate text-right" title={arrival.place}>
            {arrival.place}
          </span>
        </div>
      )}
      {(rule ?? links) && (
        <div className="mt-1.5 flex min-h-[18px] items-center gap-3">
          {rule && (
            <span className="min-w-0 truncate text-detail font-medium text-foreground">{rule}</span>
          )}
          {links && <span className="ml-auto flex shrink-0 items-center gap-3">{links}</span>}
        </div>
      )}
    </div>
  );
}

/** A pill das paradas no trilho («1 parada · ATL», «1 conexão»). */
export function StopsPill({ children }: { readonly children: ReactNode }) {
  return (
    <span className="rounded-full border border-border bg-card px-[5px] py-0.5 text-detail font-medium whitespace-nowrap text-foreground">
      {children}
    </span>
  );
}

/** O link da régua («Detalhes»): 12/16 Medium muted sublinhado. */
export const RULE_LINK =
  'cursor-pointer border-0 bg-transparent p-0 text-xs leading-4 font-medium text-muted-foreground ' +
  'underline underline-offset-3 transition-colors hover:text-foreground ' +
  'focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--brand)';

/** As pernas lado a lado (ida · volta) quando o card dá — divisor vertical a
 *  a partir da régua de componente do DS (`--breakpoint-sm`, medida na área
 *  interna do card, o container `checkout`), horizontal no estreito — ou empilhadas (multi-trecho:
 *  duas colunas com três pernas viram zigue-zague, decisão do DesignHub). */
export function CheckoutTripLegs({
  pair,
  children,
}: {
  readonly pair: boolean;
  readonly children: ReactNode;
}) {
  const divided = '*:not-first:border-t *:not-first:border-border *:not-first:pt-3';
  return (
    <div
      className={
        pair
          ? `grid gap-3 @min-[theme(--breakpoint-sm)]/checkout:grid-cols-2 @min-[theme(--breakpoint-sm)]/checkout:gap-4 ${divided} @min-[theme(--breakpoint-sm)]/checkout:*:not-first:border-t-0 @min-[theme(--breakpoint-sm)]/checkout:*:not-first:border-l @min-[theme(--breakpoint-sm)]/checkout:*:not-first:pt-0 @min-[theme(--breakpoint-sm)]/checkout:*:not-first:pl-4`
          : `grid gap-3 ${divided}`
      }
    >
      {children}
    </div>
  );
}
